import type { AvailableParameter } from '../../../shared/ui/propertySelection/PropertySelectionPanel';
import type { PipingSystem } from '../types';
import {
  COMPONENT_PROPS_KEY,
  createComponentColumnLabel,
  type ComponentParameterValues,
} from './componentColumns';

const RESERVED_SYSTEM_KEYS = new Set([
  'systemName',
  'sourceSystemName',
  'systemTypeName',
  'elementCount',
  'status',
  'warningCount',
  'errorCount',
  'elementIds',
  'componentProps',
  COMPONENT_PROPS_KEY,
  '__revitId',
  '__props',
  'PIPING-SPEC',
  'MATERIAL',
  'DESIGN-STD',
  'PRESSURE-CLASS',
  'INSULATION-SPEC',
]);

function inferDataTypeFromValue(value: unknown): string {
  if (typeof value === 'number') {
    return Number.isInteger(value) ? 'integer' : 'double';
  }
  if (typeof value === 'boolean') {
    return 'boolean';
  }
  return 'string';
}

export function collectFallbackParametersFromSystems(systems: PipingSystem[]): AvailableParameter[] {
  const parameterMap = new Map<string, AvailableParameter>();

  const addParameter = (name: string, value: unknown, category = 'System') => {
    const normalized = String(name || '').trim();
    if (!normalized || RESERVED_SYSTEM_KEYS.has(normalized)) {
      return;
    }
    const key = `${category.toLowerCase()}:${normalized.toLowerCase()}`;
    if (parameterMap.has(key)) {
      return;
    }

    parameterMap.set(key, {
      name: normalized,
      dataType: inferDataTypeFromValue(value),
      category,
      isReadOnly: false,
    });
  };

  const addComponentParameter = (name: string, values: unknown) => {
    const normalized = String(name || '').trim();
    if (!normalized) {
      return;
    }

    const valueList = Array.isArray(values) ? values : [];
    const sampleValue = valueList.find((value) => String(value ?? '').trim().length > 0) ?? '';
    addParameter(createComponentColumnLabel(normalized), sampleValue, 'Component parameters');
  };

  systems.forEach((system) => {
    const props = (system as Record<string, unknown>).__props as Record<string, unknown> | undefined;
    if (props && typeof props === 'object') {
      Object.entries(props).forEach(([key, value]) => addParameter(key, value));
    } else {
      Object.entries(system).forEach(([key, value]) => addParameter(key, value));
    }

    const componentProps = (system.componentProps ?? system[COMPONENT_PROPS_KEY]) as ComponentParameterValues | undefined;
    if (componentProps && typeof componentProps === 'object') {
      Object.entries(componentProps).forEach(([key, values]) => addComponentParameter(key, values));
    }
  });

  return Array.from(parameterMap.values()).sort((a, b) => a.name.localeCompare(b.name));
}

export function mergeParameters(primary: AvailableParameter[], fallback: AvailableParameter[]): AvailableParameter[] {
  const merged = new Map<string, AvailableParameter>();
  primary.forEach((item) => merged.set(item.name, item));
  fallback.forEach((item) => {
    if (!merged.has(item.name)) {
      merged.set(item.name, item);
    }
  });
  return Array.from(merged.values()).sort((a, b) => a.name.localeCompare(b.name));
}
